import React from 'react';
import { TextValidator } from 'react-material-ui-form-validator';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import { SpectralRanges } from '@ledium/types';
import { DetailedIntensityInfo } from '@ledium/ui-common';
import { Grid, Stack, Switch } from '@mui/material';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';

interface IntensityRanges {
    pfd: SpectralRanges;
    irradiance: SpectralRanges;
}

interface IntensityChannel {
    id: string;
    resolution: number;
    intensity: number;
    intensityPercent: number;
    intensityRanges: IntensityRanges;
    spectrogramRanges: IntensityRanges;
}

interface LightChannelIntensityInputProps {
    channel: IntensityChannel;
    readOnly?: boolean;
    onIntensityChange: (id: string, value: number) => void;
}

const LightChannelIntensityInput = ({ channel, readOnly, onIntensityChange }: LightChannelIntensityInputProps) => {
    const {
        pageState: { formData }
    } = usePageStore();
    const numButtons = [-10, -1, 1, 10];
    const settings = formData.settings as { intensityUnit: 'pfd' | 'irradiance' };

    function onChangeTextInput(e: React.ChangeEvent<HTMLInputElement>) {
        onIntensityChange(channel.id, Number(e.target.value));
    }

    function onChangeSlider(_e: React.SyntheticEvent, value: boolean | number) {
        onIntensityChange(channel.id, Number(value));
    }

    if (channel.resolution === 1) {
        return (
            <Grid item xs={12}>
                <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
                    <Switch
                        checked={!!channel.intensityPercent}
                        onChange={onChangeSlider as unknown as React.ChangeEventHandler<HTMLInputElement>}
                        disabled={!!readOnly}
                        color="primary"
                    />
                    <DetailedIntensityInfo labelKey="intensity" spectrogram={channel.intensityRanges} unit={settings.intensityUnit} />
                </Stack>
            </Grid>
        );
    }

    return readOnly ? (
        <Grid item xs={12}>
            <DetailedIntensityInfo labelKey="intensity" spectrogram={channel.intensityRanges} unit={settings.intensityUnit} />
        </Grid>
    ) : (
        <>
            <Grid item xs={6} display="flex" justifyContent="center">
                <ButtonGroup size="small">
                    {numButtons.map((num, idx) => (
                        <Button key={idx} onClick={() => onIntensityChange(channel.id, channel.intensityRanges[settings.intensityUnit].sum + num)}>
                            {num > 0 ? `+${num}` : num}
                        </Button>
                    ))}
                </ButtonGroup>
            </Grid>
            <Grid item xs={6}>
                <Stack spacing={1} direction="row" alignItems="center">
                    <TextValidator
                        size="small"
                        label={`${i18n('intensity')} (${i18n(settings.intensityUnit + '_unit')})`}
                        type="number"
                        onChange={onChangeTextInput}
                        value={Math.round(channel.intensityRanges[settings.intensityUnit].sum)}
                        variant="outlined"
                        min={0}
                        max={channel.intensityRanges[settings.intensityUnit].sum}
                        validators={['required', 'isNumber', 'minNumber:0', `maxNumber:${channel.spectrogramRanges[settings.intensityUnit].sum}`]}
                        errorMessages={[i18n('this_field_is_required'), i18n('invalid_number'), i18n('the_value_should_be_minimum_X', 0)]}
                    />
                    <DetailedIntensityInfo spectrogram={channel.intensityRanges} unit={settings.intensityUnit} iconOnly />
                </Stack>
            </Grid>
        </>
    );
};

export default LightChannelIntensityInput;
